import { UserRoleEnum } from 'generated/api';
import { PERMISSIONS, Permission } from './StaffUtils';
import { RootState } from '../careand-redux/store';
import { useAppSelector } from '../careand-redux/store';

export type PermissionRule = {
  roles?: UserRoleEnum[];
  permissions?: string[];
  customCheck?: (permissions?: string[], role?: UserRoleEnum) => boolean;
};

export function validatePermission(
  user: { role?: UserRoleEnum; permissions?: string[] },
  check: PermissionRule,
) {
  if (check.customCheck) {
    return check.customCheck(user.permissions, user.role);
  }

  let hasRole = !check.roles || check.roles.length === 0 || check.roles.includes(user.role!);
  if (!hasRole) {
    return false;
  }

  let hasPermission =
    !check.permissions ||
    check.permissions.length === 0 ||
    !!user.permissions?.some((permission) => check.permissions!.includes(permission));
  if (!hasPermission) {
    return false;
  }

  return true;
}

export interface FeatureConfig extends PermissionRule {
  title: string;
  icon: string;
  description: string;
  route?: string;
  badgeSelector?: (state: any) => number;
}

export const FEATURE_PERMISSIONS: Record<string, FeatureConfig> = {
  // Statistics & Analytics
  statistics: {
    title: 'Clinic Dashboard',
    icon: 'chart-pie',
    description: "See Clinic's performance statistics",
    permissions: [PERMISSIONS.super_admin, PERMISSIONS.shift_manager, PERMISSIONS.statistics],
    route: 'clinicStatisticsDashboard',
  },

  // Scheduling & Shifts
  shifts: {
    title: 'Shifts',
    icon: 'calendar-clock',
    description: "Edit the Staff's shifts",
    permissions: [PERMISSIONS.shift_manager],
    route: 'shifts',
    badgeSelector: (state) => state.shifts.pendingRequests.length,
  },

  // Booking & Appointments
  bookingOptions: {
    title: 'Booking Flow',
    icon: 'pen-to-square',
    description: 'Add or edit the booking flow options',
    permissions: [PERMISSIONS.super_admin],
    route: 'bookingOptions',
  },

  serviceFees: {
    title: 'Service Fees',
    icon: 'syringe',
    description: 'Set immunization prices and manage administered drugs',
    permissions: [PERMISSIONS.super_admin],
    route: 'serviceFees',
  },

  concernRequirements: {
    title: 'Clearances',
    icon: 'shield-check',
    description: 'Manage clearances and patient requirements',
    permissions: [PERMISSIONS.super_admin],
    route: 'concernRequirements',
  },

  // Corporate Management
  corporate: {
    title: 'Corporate',
    icon: 'city',
    description: 'Manage Corporate clients (patients, invoices, etc)',
    permissions: [PERMISSIONS.corporate],
    route: 'corporate',
    badgeSelector: (state: RootState) =>
      Object.values(state.corporations.unpaidCount).reduce(
        (acc, val) => (acc || 0) + (val || 0),
        0,
      ) || 0,
  },

  // Health & Clinical
  healthMetrics: {
    title: 'Health Metrics',
    icon: 'chart-line',
    description: 'Edit Health Metrics available to patients',
    permissions: [PERMISSIONS.super_admin],
    route: 'healthMetrics',
  },

  // Payment & Billing
  paymentPlans: {
    title: 'Payment Plans',
    icon: 'wallet',
    description: "Edit the payment plans' permissions costs",
    permissions: [PERMISSIONS.super_admin],
    route: 'paymentPlans',
  },

  overdueSubscriptions: {
    title: 'Overdue Subs',
    icon: 'calendar-exclamation',
    description: 'See subscriptions that are overdue',
    permissions: [PERMISSIONS.super_admin],
    route: 'overdueSubscriptions',
  },

  // Templates & Documents
  templates: {
    title: 'Templates',
    icon: 'files',
    description: 'Add or edit document templates',
    customCheck: (permissions, role) => {
      const req = [PERMISSIONS.super_admin, PERMISSIONS.shift_manager] as string[];
      if (role === UserRoleEnum.Physician) {
        return !!permissions?.some((permission) => req.includes(permission));
      }
      return role === UserRoleEnum.Admin;
    },
    route: 'templates',
    badgeSelector: (state) => {
      if (!state.authentication.permissions.includes(PERMISSIONS.super_admin)) {
        return 0;
      }
      return state.templates.templates.reduce((acc: number, template: any) => {
        return acc + (template?.is_pending ? 1 : 0);
      }, 0);
    },
  },

  // Clinic Management
  clinicDetails: {
    title: 'Clinics',
    icon: 'hospital',
    description: 'Manage all clinics and their details',
    permissions: [PERMISSIONS.super_admin],
    route: 'clinicDetails',
  },

  // AI & Prompts
  promptDashboard: {
    title: 'Prompt Dashboard',
    icon: 'brain-circuit',
    description: 'Manage & Monitor AI prompts',
    permissions: [PERMISSIONS.super_admin],
    route: 'promptDashboard',
  },

  // Assets & Media
  assetsManager: {
    title: 'Assets Manager',
    icon: 'images',
    description: 'Browse & Manage Assets',
    permissions: [PERMISSIONS.super_admin],
    route: 'assetsManager',
  },

  // Contacts & External
  contacts: {
    title: 'Contacts',
    icon: 'users-medical',
    description: 'Manage external contacts for referrals, etc',
    permissions: [], // Always allowed
    route: 'contacts',
  },

  // Patient Management
  addPatient: {
    title: 'Add Patient',
    icon: 'user-plus',
    description: 'Register a new account on behalf of a patient',
    permissions: [], // Always allowed
  },

  // Automation & Workflows
  hooksManagement: {
    title: 'Hook Management',
    icon: 'webhook',
    description: 'Create and manage automated workflows and notifications',
    permissions: [PERMISSIONS.super_admin],
    route: 'hooksManagement',
  },
  tenantOptions: {
    title: 'Options',
    icon: 'sliders',
    description: 'Miscellaneous settings for your organization (email social links, etc)',
    permissions: [PERMISSIONS.super_admin],
    route: 'tenantOptions',
  },
};

/**
 * Check if a user has permission to access a feature
 */
export function checkFeaturePermission(
  featureKey: string,
  userPermissions: string[],
  userRole: string,
): boolean {
  const feature = FEATURE_PERMISSIONS[featureKey];
  if (!feature) return false;

  return validatePermission(
    { permissions: userPermissions, role: userRole as UserRoleEnum },
    feature,
  );
}

/**
 * Get all features that a user has access to
 */
export function getAccessibleFeatures(
  userPermissions: string[],
  userRole: string,
): Record<string, FeatureConfig> {
  const accessible: Record<string, FeatureConfig> = {};

  Object.entries(FEATURE_PERMISSIONS).forEach(([key, feature]) => {
    if (checkFeaturePermission(key, userPermissions, userRole)) {
      accessible[key] = feature;
    }
  });

  return accessible;
}

export function useHasPermission(permission: PermissionRule) {
  const hasPermission = useAppSelector((state) =>
    validatePermission(state.authentication, permission),
  );
  return hasPermission;
}

/**
 * Hook to get feature permissions for current user
 */
export function useFeaturePermissions() {
  return {
    checkFeaturePermission,
    getAccessibleFeatures,
    FEATURE_PERMISSIONS,
  };
}
